import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import ErrorBanner from '../../components/shared/ErrorBanner'
import InlineLoading from '../../components/shared/InlineLoading'
import useAuth from '../../hooks/useAuth'
import getEnvironmentVariables from '../../helpers/getEnvironmentVariables'
import {
    STATUS,
    canManageHolidays,
    holidayManagerRole,
    isoDateIst,
    timeIst,
    toggledStatus,
} from '../../helpers/holidays'
import HolidayService from '../../services/holidayService'
import { errorMessage } from '../../services/axios/errorMessage'
import {
    FONT,
    ICON,
    STATUS as TONE,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
} from '../../theme/tokens'
import EditHolidayDialog from './EditHolidayDialog'
import HolidayHeader from './HolidayHeader'

const PAGE_SIZE = 10
const SEARCH_DELAY_MS = 400

const STATUS_COLOR = {
    [STATUS.ACTIVE]: TONE.success.fg,
    [STATUS.INACTIVE]: TONE.warning.fg,
    [STATUS.COMPLETED]: TEXT.muted,
}

/** The list a tab shows: the server's active list, or inactive (which includes completed). */
const TABS = [
    { status: STATUS.ACTIVE, label: 'Active' },
    { status: STATUS.INACTIVE, label: 'Inactive and completed' },
]

const cellSx = { borderColor: SURFACE.border, typography: 'caption' }

const StatusText = ({ status }) => (
    <Typography
        sx={{
            typography: 'caption',
            fontWeight: 600,
            color: STATUS_COLOR[status] ?? TEXT.secondary,
        }}
    >
        {status}
    </Typography>
)
StatusText.propTypes = { status: PropTypes.string }

/**
 * Holidays: the holidays the comms go out for (CommHolidays), each sent by
 * job-holiday-reminder through the pipeline whose event type is the holiday
 * id. Ported from the applause comms-app, which comms.bhanzu.com served
 * before Comms V2.
 *
 * Anyone with the Operate roles can view and search. Creating, editing and
 * switching a holiday on or off needs comms-tool-dev on dev or
 * comms-tool-prod on production, as in the applause app.
 */
const Holidays = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { user } = useAuth()
    const env = getEnvironmentVariables()
    const canManage = canManageHolidays(user?.permissions, env)

    const [tab, setTab] = useState(0)
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [page, setPage] = useState(0)
    const [editing, setEditing] = useState(null)
    const [switching, setSwitching] = useState(null)
    const [busy, setBusy] = useState(false)
    const [notice, setNotice] = useState(location.state?.holidayNotice ?? '')
    const [noticeError, setNoticeError] = useState(
        location.state?.holidayError ?? ''
    )

    const status = TABS[tab].status

    useEffect(() => {
        const t = setTimeout(
            () => setDebouncedQuery(query.trim()),
            SEARCH_DELAY_MS
        )
        return () => clearTimeout(t)
    }, [query])

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            // The whole tab is loaded once and searched on the page: the API's
            // search is case-sensitive ("diwali" found nothing).
            const res = await HolidayService.list(status)
            const list = res?.data?.holidays ?? []
            // Active: the next holiday first. Inactive and completed: the most
            // recent first (oldest first put a holiday just switched off on the
            // last page, behind years of 2023 rows).
            const direction = status === STATUS.ACTIVE ? 1 : -1
            setRows(
                [...list].sort(
                    (a, b) =>
                        direction *
                        (Number(a.holiday_date) - Number(b.holiday_date))
                )
            )
        } catch (err) {
            setRows([])
            setError(errorMessage(err?.response))
        } finally {
            setLoading(false)
        }
    }, [status])

    useEffect(() => {
        load()
    }, [load])

    const onSwitch = async () => {
        const holiday = switching
        setBusy(true)
        try {
            await HolidayService.setStatus(
                holiday.holiday_id,
                toggledStatus(holiday.event_status)
            )
            setNotice(
                `${
                    holiday.holiday_name || holiday.holiday_id
                } is now ${toggledStatus(holiday.event_status)}.`
            )
            setSwitching(null)
            load()
        } catch (err) {
            setSwitching(null)
            setError(errorMessage(err?.response))
        } finally {
            setBusy(false)
        }
    }

    // Name or id contains the search text, ignoring case.
    const matching = useMemo(() => {
        const needle = debouncedQuery.toLowerCase()
        if (!needle) return rows
        return rows.filter((h) =>
            `${h.holiday_name ?? ''} ${h.holiday_id ?? ''}`
                .toLowerCase()
                .includes(needle)
        )
    }, [rows, debouncedQuery])

    useEffect(() => setPage(0), [status, debouncedQuery])

    const visible = matching.slice(
        page * PAGE_SIZE,
        page * PAGE_SIZE + PAGE_SIZE
    )
    const role = holidayManagerRole(env)

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 3, md: 4 } }}>
                <HolidayHeader
                    title="Holidays"
                    subtitle="Holiday comms go out through the pipeline whose event type is the holiday ID. Times are IST."
                    actions={
                        <>
                            {/* Viewers too: the list may change under them. */}
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={load}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                            {canManage && (
                                <>
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            navigate('/holidays/bulk')
                                        }
                                    >
                                        Bulk upload
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() =>
                                            navigate('/holidays/new')
                                        }
                                    >
                                        New holiday
                                    </Button>
                                </>
                            )}
                        </>
                    }
                />

                {!canManage && (
                    <Typography
                        sx={{
                            typography: 'caption',
                            color: TEXT.secondary,
                            mb: 2,
                        }}
                    >
                        {role
                            ? `You can view holidays. Creating or changing them needs the ${role} role.`
                            : 'Holidays can only be changed on dev and production.'}
                    </Typography>
                )}
                {notice && (
                    <Typography
                        role="status"
                        sx={{
                            typography: 'body2',
                            color: TONE.success.fg,
                            mb: 2,
                        }}
                    >
                        {notice}
                    </Typography>
                )}
                {noticeError && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={noticeError} />
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Tabs
                        value={tab}
                        onChange={(_, v) => {
                            setTab(v)
                            setNotice('')
                            setNoticeError('')
                        }}
                        aria-label="Holiday status"
                    >
                        {TABS.map((t) => (
                            <Tab key={t.status} label={t.label} />
                        ))}
                    </Tabs>
                    <TextField
                        size="small"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by holiday name"
                        inputProps={{ 'aria-label': 'Search by holiday name' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            fontSize: ICON.lg,
                                            color: TEXT.muted,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 300 }}
                    />
                </Box>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small" sx={{ minWidth: 960 }}>
                            <TableHead>
                                <TableRow>
                                    {[
                                        'Holiday',
                                        'Holiday ID',
                                        'Status',
                                        'Date',
                                        'Comms time',
                                        'Comms days before',
                                        'Reminder days before',
                                        'Countries',
                                        '',
                                    ].map((h) => (
                                        <TableCell
                                            key={h || 'actions'}
                                            sx={cellSx}
                                        >
                                            <Typography variant="overline">
                                                {h}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} sx={cellSx}>
                                            <InlineLoading
                                                label={`Loading ${TABS[
                                                    tab
                                                ].label.toLowerCase()} holidays…`}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ) : visible.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={cellSx}
                                            align="center"
                                        >
                                            <Typography
                                                sx={{
                                                    typography: 'body2',
                                                    color: TEXT.muted,
                                                    py: 3,
                                                }}
                                            >
                                                {debouncedQuery
                                                    ? `No ${TABS[
                                                          tab
                                                      ].label.toLowerCase()} holidays match "${debouncedQuery}".`
                                                    : `No ${TABS[
                                                          tab
                                                      ].label.toLowerCase()} holidays.`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visible.map((h) => {
                                        const [comms, reminder] = h.events ?? []
                                        return (
                                            <TableRow
                                                key={h.holiday_id}
                                                data-testid="holiday-row"
                                            >
                                                <TableCell
                                                    sx={{
                                                        ...cellSx,
                                                        color: TEXT.primary,
                                                    }}
                                                >
                                                    {h.holiday_name}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...cellSx,
                                                        color: TEXT.secondary,
                                                        fontFamily: FONT.mono,
                                                    }}
                                                >
                                                    {h.holiday_id}
                                                </TableCell>
                                                <TableCell sx={cellSx}>
                                                    <StatusText
                                                        status={h.event_status}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...cellSx,
                                                        color: TEXT.secondary,
                                                    }}
                                                >
                                                    {h.holiday_date
                                                        ? isoDateIst(
                                                              h.holiday_date
                                                          )
                                                        : '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...cellSx,
                                                        color: TEXT.secondary,
                                                    }}
                                                >
                                                    {comms
                                                        ? timeIst(
                                                              comms.event_time
                                                          )
                                                        : '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...cellSx,
                                                        color: TEXT.secondary,
                                                    }}
                                                >
                                                    {comms
                                                        ? comms.days_before_holiday
                                                        : '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...cellSx,
                                                        color: TEXT.secondary,
                                                    }}
                                                >
                                                    {reminder
                                                        ? reminder.days_before_holiday
                                                        : '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...cellSx,
                                                        color: TEXT.secondary,
                                                    }}
                                                >
                                                    {h.allowed_countries?.length
                                                        ? h.allowed_countries.join(
                                                              ', '
                                                          )
                                                        : 'All'}
                                                </TableCell>
                                                <TableCell
                                                    sx={cellSx}
                                                    align="right"
                                                >
                                                    {canManage && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 1,
                                                                justifyContent:
                                                                    'flex-end',
                                                            }}
                                                        >
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() =>
                                                                    setEditing(
                                                                        h
                                                                    )
                                                                }
                                                                aria-label={`Edit ${h.holiday_id}`}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() =>
                                                                    setSwitching(
                                                                        h
                                                                    )
                                                                }
                                                                aria-label={`Mark ${
                                                                    h.holiday_id
                                                                } ${toggledStatus(
                                                                    h.event_status
                                                                )}`}
                                                            >
                                                                Mark{' '}
                                                                {toggledStatus(
                                                                    h.event_status
                                                                )}
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {matching.length > PAGE_SIZE && (
                        <TablePagination
                            component="div"
                            count={matching.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={PAGE_SIZE}
                            rowsPerPageOptions={[PAGE_SIZE]}
                        />
                    )}
                </Paper>
            </Box>

            {editing && (
                <EditHolidayDialog
                    holiday={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setNotice(
                            `${
                                editing.holiday_name || editing.holiday_id
                            } saved.`
                        )
                        setEditing(null)
                        load()
                    }}
                />
            )}
            <ConfirmDialog
                open={!!switching}
                title={
                    switching
                        ? `Mark ${
                              switching.holiday_name || switching.holiday_id
                          } ${toggledStatus(switching.event_status)}?`
                        : ''
                }
                message={
                    switching
                        ? toggledStatus(switching.event_status) ===
                          STATUS.ACTIVE
                            ? 'Its comms will go out again on their scheduled days.'
                            : 'Its comms will not go out while it is inactive.'
                        : ''
                }
                confirmLabel={
                    busy
                        ? 'Saving...'
                        : switching
                        ? `Mark ${toggledStatus(switching.event_status)}`
                        : 'Confirm'
                }
                destructive={
                    !!switching && switching.event_status === STATUS.ACTIVE
                }
                onConfirm={busy ? () => {} : onSwitch}
                onCancel={() => setSwitching(null)}
            />
        </Box>
    )
}

export default Holidays
